import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Loader2, Monitor, Smartphone, Globe2, Shield, RefreshCw } from 'lucide-react';
import { sessionsApi } from '../../lib/api';
import { toast } from 'sonner';

// Pretty "2 hours ago" relative time without bringing in date-fns.
function relativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function deviceIcon(label) {
  const lower = (label || '').toLowerCase();
  if (lower.includes('android') || lower.includes('ios') || lower.includes('iphone') || lower.includes('mobile')) {
    return <Smartphone className="w-4 h-4 text-indigo-400" />;
  }
  return <Monitor className="w-4 h-4 text-indigo-400" />;
}

export default function RecentSignInsCard() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await sessionsApi.list();
      setSessions(r.sessions || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load sign-ins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onRevokeOthers = async () => {
    setRevoking(true);
    try {
      const r = await sessionsApi.revokeOthers();
      if (r.sessions_revoked > 0) {
        toast.success(`Signed out ${r.sessions_revoked} other ${r.sessions_revoked === 1 ? 'session' : 'sessions'}.`);
      } else {
        toast.info('No other active sessions to sign out.');
      }
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Could not revoke sessions');
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div
      className="p-6 rounded-xl border border-border bg-card space-y-4"
      data-testid="recent-signins-card"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-indigo-400" />
          <h2 className="text-lg font-semibold">Recent Sign-Ins</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={refresh}
            disabled={loading}
            data-testid="signins-refresh-btn"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onRevokeOthers}
            disabled={revoking || loading || sessions.filter(s => !s.is_current).length === 0}
            data-testid="signins-revoke-others-btn"
          >
            {revoking ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Revoking…</>
            ) : (
              <>Sign out other devices</>
            )}
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Last 5 sign-ins to your account. If you don&apos;t recognise one,
        change your password and click &ldquo;Sign out other devices&rdquo;.
      </p>

      {loading && sessions.length === 0 ? (
        <div className="py-8 flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />Loading…
        </div>
      ) : sessions.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground" data-testid="signins-empty">
          No sign-ins recorded yet.
        </div>
      ) : (
        <ul className="divide-y divide-border" data-testid="signins-list">
          {sessions.map((s, i) => (
            <li
              key={`${s.created_at}-${i}`}
              className="py-3 flex items-start justify-between gap-3"
              data-testid={`signin-row-${i}`}
            >
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="mt-0.5">{deviceIcon(s.device)}</div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate" data-testid={`signin-device-${i}`}>
                    {s.device}
                    {s.is_current && (
                      <span
                        className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                        data-testid={`signin-current-badge-${i}`}
                      >
                        This device
                      </span>
                    )}
                    {s.channel === 'google' && (
                      <span className="ml-2 text-[10px] text-muted-foreground">via Google</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                    <span className="inline-flex items-center gap-1">
                      <Globe2 className="w-3 h-3" />
                      {s.country || 'Unknown'}
                    </span>
                    <span>·</span>
                    <span className="font-mono">{s.ip || '0.0.0.0'}</span>
                    <span>·</span>
                    <span title={new Date(s.created_at).toLocaleString()}>
                      {relativeTime(s.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
